import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, MessageSquare } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { timeAgo } from '../utils/timeAgo';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface CustomerChatHistoryProps {
  userId: string;
}

/**
 * Mirrors the server default in `listSupportChatConversationsByUser`
 * (`packages/db-operations/src/support-chat-operations.ts`). The endpoint pages
 * nothing and returns at most this many conversations, so a customer with more
 * history than this has some of it silently omitted. Because the panel renders
 * oldest-first, that omission lands at the TOP of the list, where it reads as
 * the beginning of the customer's history rather than as a cut-off — so it has
 * to be labelled or an agent will under-count how often this customer writes in.
 */
const CONVERSATION_FETCH_LIMIT = 50;

/**
 * Collapsible dropdown of a customer's support AI-chat conversations. Both
 * levels load lazily via `enabled` — conversations on first open, each
 * conversation's turns on expand — so the sidebar stays cheap for tickets from
 * chatty users, while React Query caches what was already fetched.
 *
 * Both levels read bottom-anchored, like a message thread: conversations run
 * oldest → newest down the panel (which opens scrolled to the newest), and each
 * transcript is already chronological from the endpoint.
 */
export function CustomerChatHistory({ userId }: CustomerChatHistoryProps) {
  const [open, setOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const didScrollRef = useRef(false);

  const conversationsQuery = useQuery({
    queryKey: queryKeys.customerSupport.adminUserChat(userId),
    queryFn: async () => (await api.getSupportUserChatConversations(userId)).conversations,
    enabled: open,
  });

  const messagesQuery = useQuery({
    queryKey: queryKeys.customerSupport.adminChatMessages(expandedId ?? 'none'),
    queryFn: async () => (await api.getSupportChatMessages(expandedId!)).messages,
    enabled: expandedId !== null,
  });

  const conversations = conversationsQuery.data;
  const count = conversations?.length ?? null;

  // The endpoint returns the most recently active conversations FIRST, and it
  // must keep doing so — its LIMIT has to select the newest ones, not the
  // oldest. Display order is the opposite question, and here it should read like
  // any message thread: oldest at the top, newest at the bottom. So reverse for
  // rendering only, and leave the fetch alone.
  const orderedConversations = useMemo(
    () => (conversations ? [...conversations].reverse() : undefined),
    [conversations],
  );

  // Bottom-anchored order is only an improvement if the newest conversation is
  // the one you land on; without this the panel opens scrolled to the OLDEST
  // chat, which is worse than the newest-first list it replaced.
  //
  // Fires ONCE per open, guarded by a ref rather than by the dep array. Two
  // things would otherwise move the scroll out from under the reader:
  //   - expanding a conversation grows the container (`expandedId` is therefore
  //     not a dep), and
  //   - a background refetch produces a new `conversations` reference whenever
  //     any row's `updated_at` changes. The client refetches on every window
  //     focus (`refetchOnWindowFocus: 'always'`), so an agent who alt-tabs away
  //     while the customer is still chatting would come back to find the panel
  //     had jumped to the bottom, losing the transcript they were reading.
  // The ref resets on close so the next open re-anchors.
  useEffect(() => {
    if (!open) {
      didScrollRef.current = false;
      return;
    }
    if (didScrollRef.current || !orderedConversations?.length) return;
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
      didScrollRef.current = true;
    }
  }, [open, orderedConversations]);

  return (
    <div className="space-y-1">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={open}
      >
        <h4 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
          <MessageSquare className="h-3.5 w-3.5" />
          Chat history{count !== null ? ` (${count})` : ''}
        </h4>
        <ChevronRight
          className={`h-4 w-4 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>

      {open && (
        <div ref={scrollRef} className="max-h-[320px] overflow-y-auto space-y-1.5 pt-1">
          {conversationsQuery.isLoading ? (
            <>
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </>
          ) : conversationsQuery.isError ? (
            <p className="text-xs text-destructive">Couldn’t load chat history.</p>
          ) : orderedConversations && orderedConversations.length > 0 ? (
            <>
              {orderedConversations.length >= CONVERSATION_FETCH_LIMIT && (
                <p className="px-1 pb-1 text-center text-[11px] text-muted-foreground">
                  Showing the {CONVERSATION_FETCH_LIMIT} most recent conversations. Older history is
                  not listed.
                </p>
              )}
              {orderedConversations.map((c) => (
                <div key={c.conversationId} className="rounded-md border">
                  <button
                    onClick={() =>
                      setExpandedId((prev) => (prev === c.conversationId ? null : c.conversationId))
                    }
                    className="flex w-full items-start gap-2 p-2 text-left text-xs hover:bg-muted/50"
                    aria-expanded={expandedId === c.conversationId}
                  >
                    <ChevronRight
                      className={`h-3 w-3 mt-0.5 flex-shrink-0 text-muted-foreground transition-transform ${
                        expandedId === c.conversationId ? 'rotate-90' : ''
                      }`}
                    />
                    <div className="min-w-0 flex-1">
                      <div className="line-clamp-1 font-medium">
                        {c.firstMessage || 'Support chat'}
                      </div>
                      <div className="mt-0.5 text-muted-foreground">
                        {c.messageCount} message{c.messageCount === 1 ? '' : 's'} ·{' '}
                        {timeAgo(c.updatedAt)}
                      </div>
                    </div>
                  </button>

                  {expandedId === c.conversationId && (
                    <div className="space-y-2 border-t p-2">
                      {messagesQuery.isLoading ? (
                        <Skeleton className="h-16 w-full" />
                      ) : messagesQuery.isError ? (
                        <p className="text-xs text-destructive">Couldn’t load this conversation.</p>
                      ) : (messagesQuery.data?.length ?? 0) === 0 ? (
                        <p className="text-xs text-muted-foreground">No messages recorded.</p>
                      ) : (
                        messagesQuery.data!.map((m) => (
                          <div key={m.messageId} className="text-xs">
                            <div className="font-medium text-muted-foreground">
                              {m.role === 'user' ? 'Customer' : 'Fias AI'}
                            </div>
                            <div className="whitespace-pre-wrap break-words">{m.content}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : (
            <p className="text-xs text-muted-foreground">No AI chat history from this customer.</p>
          )}
        </div>
      )}
    </div>
  );
}
